import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ViewDataContent from '../../components/ViewDataContent';
import AddDataContent from '../../components/AddDataContent';
import SidebarLayout from '../../components/SidebarLayout';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const TablesPage = () => {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState(null);
  const [addingDataSchemaId, setAddingDataSchemaId] = useState(null);

  useEffect(() => {
    fetchSchemas();
    fetchSchoolInfo();
  }, []);

  const fetchSchemas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/schemas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schemas');
      }

      const data = await response.json();
      setSchemas(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/entity`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch school information');
      }

      const data = await response.json();
      setSchoolInfo(data.data);
    } catch (err) {
      console.error('Failed to fetch school info:', err.message);
    }
  };

  const handleAddData = (schemaId) => {
    // Toggle: if clicking the same schema, close it; otherwise, open the clicked one
    if (addingDataSchemaId === schemaId) {
      setAddingDataSchemaId(null);
    } else {
      setAddingDataSchemaId(schemaId);
      // Close view data if it's open for the same schema
      if (selectedSchemaId === schemaId) {
        setSelectedSchemaId(null);
      }
    }
  };

  const handleAddDataSuccess = () => {
    // Refresh the view data if it's open
    if (selectedSchemaId) {
      // Trigger a re-render by toggling
      const currentId = selectedSchemaId;
      setSelectedSchemaId(null);
      setTimeout(() => {
        setSelectedSchemaId(currentId);
      }, 100);
    }
  };

  const handleViewData = (schemaId) => {
    if (selectedSchemaId === schemaId) {
      setSelectedSchemaId(null);
    } else {
      setSelectedSchemaId(schemaId);
    }
  };

  const handleEditTable = (schema) => {
    if (schema?.is_org_template === true) return;
    navigate(`/visuals/edit-table/${schema.schema_id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getColumnCount = (schemaDefinition) => {
    try {
      const definition = typeof schemaDefinition === 'string' 
        ? JSON.parse(schemaDefinition) 
        : schemaDefinition;
      return definition.columns ? definition.columns.length : 0;
    } catch {
      return 0;
    }
  };

  // Render skeleton loaders
  const renderSkeleton = () => {
    return (
      <div className="tables-grid">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="table-card skeleton-card">
            <div className="skeleton-header">
              <div className="skeleton-line skeleton-title"></div>
              <div className="skeleton-meta">
                <div className="skeleton-line skeleton-badge"></div>
                <div className="skeleton-line skeleton-badge"></div>
              </div>
            </div>
            <div className="skeleton-body">
              <div className="skeleton-line skeleton-text"></div>
              <div className="skeleton-line skeleton-text"></div>
            </div>
            <div className="skeleton-actions">
              <div className="skeleton-line skeleton-button"></div>
              <div className="skeleton-line skeleton-button"></div>
              <div className="skeleton-line skeleton-button"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <SidebarLayout>
      <div className="tables-container">
      <div className="tables-header">
        <div>
          <h1>Data Tables</h1>
          <p>Manage your school's data tables and records</p>
          {schoolInfo && !loading && (
            <div className="tables-school-info">
              <span className="tables-school-label">School:</span>
              <span className="tables-school-name">{schoolInfo.school_name}</span>
            </div>
          )}
          {loading && schoolInfo === null && (
            <div className="tables-school-info">
              <span className="skeleton-line skeleton-school-info"></span>
            </div>
          )}
        </div>
        <div className="tables-header-actions">
          <button 
            onClick={() => navigate('/visuals/create-table')}
            className="create-table-btn"
            disabled={loading}
          >
            + Create New Table
          </button>
          {/* <button 
            onClick={() => navigate('/visuals')}
            className="tables-back-btn"
            disabled={loading}
          >
            Back to Visuals
          </button> */}
        </div>
      </div>

      {error && (
        <div className="tables-error">
          {error}
        </div>
      )}

      <div className="tables-content">
        {loading ? (
          <div className="tables-loading-message">
            <div className="inline-loading-spinner"></div>
            <p>Loading tables...</p>
          </div>
        ) : null}
        
        {loading ? renderSkeleton() : schemas.length === 0 ? (
          <div className="tables-empty-state">
            <div className="empty-state-icon">📊</div>
            <h3>No Tables Created Yet</h3>
            <p>Create your first data table to start organizing your school's information.</p>
            <button 
              onClick={() => navigate('/visuals/create-table')}
              className="create-first-table-btn"
            >
              Create Your First Table
            </button>
          </div>
        ) : (
          <div className="tables-grid">
            {schemas.map((schema) => (
              <React.Fragment key={schema.schema_id}>
                <div className="table-card">
                  <div className="table-card-header">
                    <h3 className="table-name">{schema.table_name}</h3>
                    <div className="table-meta">
                      <span className="table-columns">
                        {getColumnCount(schema.schema_definition)} columns
                      </span>
                      <span className="table-version">v{schema.version}</span>
                    </div>
                  </div>
                  
                  <div className="table-card-body">
                    <div className="table-info">
                      <div className="table-info-item">
                        <span className="table-info-label">Created:</span>
                        <span className="table-info-value">
                          {formatDate(schema.created_at)}
                        </span>
                      </div>
                      <div className="table-info-item">
                        <span className="table-info-label">Schema ID:</span>
                        <span className="table-info-value">{schema.schema_id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="table-card-actions">
                    <button
                      onClick={() => handleAddData(schema.schema_id)}
                      className={`action-btn add-data-btn ${addingDataSchemaId === schema.schema_id ? 'active' : ''}`}
                    >
                      {addingDataSchemaId === schema.schema_id ? 'Hide Form' : 'Add Data'}
                    </button>
                    <button
                      onClick={() => handleViewData(schema.schema_id)}
                      className={`action-btn view-data-btn ${selectedSchemaId === schema.schema_id ? 'active' : ''}`}
                    >
                      {selectedSchemaId === schema.schema_id ? 'Hide Data' : 'View Data'}
                    </button>
                    {schema.is_org_template !== true && (
                      <button
                        onClick={() => handleEditTable(schema)}
                        className="action-btn edit-table-btn"
                      >
                        Edit Table
                      </button>
                    )}
                  </div>
                </div>
                
                {addingDataSchemaId === schema.schema_id && (
                  <div className="inline-add-data-wrapper">
                    <AddDataContent 
                      schemaId={schema.schema_id} 
                      isInline={true}
                      onBack={() => setAddingDataSchemaId(null)}
                      onSuccess={handleAddDataSuccess}
                    />
                  </div>
                )}
                
                {selectedSchemaId === schema.schema_id && (
                  <div className="inline-view-data-wrapper">
                    <ViewDataContent 
                      schemaId={schema.schema_id} 
                      isInline={true}
                      onAddData={(id) => {
                        setSelectedSchemaId(null);
                        setAddingDataSchemaId(id);
                      }}
                      onBack={() => setSelectedSchemaId(null)}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      </div>
    </SidebarLayout>
  );
};

export default TablesPage;
